import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Compass, Target, Briefcase, Heart, Zap, Award, Brain, ChevronRight,
  Star, TrendingUp, BookOpen, Search, MapPin, GraduationCap, Plus,
  CheckCircle, Calendar, MessageCircle, Sparkles, ArrowRight, LogOut,
  PenTool, Users, Lightbulb, FolderOpen, Play, X, Send, Loader2,
  FileText, Building2, Trash2, Clock, ChevronDown, ArrowLeft, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ProfileCompletionModal from '@/components/ProfileCompletionModal';
import NotificationBell from '@/components/NotificationBell';
import GoalReminders from '@/components/GoalReminders';
import MandatoryCheckinModal from '@/components/MandatoryCheckinModal';
import Navigation from '@/components/Navigation';

const API = process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem('token');
const hdr = () => ({ Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' });

// AI Guide Bubble — persistent helper
const AIBubble = ({ message, nudge, celebration, suggestions, onAction, colors }) => {
  if (!message) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-4 mb-4"
      style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)', border: '1px solid #DDD6FE' }}>
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-orange-900 mb-0.5">Your AI Guide</p>
          <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
          {celebration && (
            <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
              <Trophy className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-xs text-green-700 font-medium">{celebration}</p>
            </div>
          )}
          {nudge && (
            <div className="mt-2 px-3 py-1.5 rounded-lg bg-orange-100 inline-block">
              <p className="text-xs font-bold text-orange-700">{nudge}</p>
            </div>
          )}
          {suggestions?.length > 0 && (
            <div className="mt-2 space-y-1">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => onAction?.(s.action)}
                  className="flex items-center gap-2 w-full p-2 rounded-lg bg-white/80 hover:bg-white transition-all text-left">
                  <ArrowRight className="w-3 h-3 text-orange-500 flex-shrink-0" />
                  <span className="text-xs text-gray-700">{s.text}</span>
                  {s.points > 0 && <Badge className="ml-auto text-[9px] bg-amber-100 text-amber-700">+{s.points} pts</Badge>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const GPSStudentDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [guidance, setGuidance] = useState({});
  const [activities, setActivities] = useState([]);
  const [journal, setJournal] = useState([]);
  const [careers, setCareers] = useState([]);
  const [postSec, setPostSec] = useState([]);
  const [courses, setCourses] = useState([]);
  const [discoveredCareers, setDiscoveredCareers] = useState([]);
  const [discoverMsg, setDiscoverMsg] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showPostSecModal, setShowPostSecModal] = useState(false);
  const [showWorkshop, setShowWorkshop] = useState(null);
  const [activityForm, setActivityForm] = useState({ title: '', category: 'club', organization: '', role: '', description: '', is_current: true });
  const [journalForm, setJournalForm] = useState({ prompt: '', response: '', mood: '' });
  const [postSecForm, setPostSecForm] = useState({ school_name: '', school_type: 'college', location: '', program_of_interest: '', application_status: 'researching', deadline: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [dR, aR, jR, cR, pR, wR] = await Promise.all([
        fetch(`${API}/api/gps/dashboard`, { headers: hdr() }),
        fetch(`${API}/api/gps/activities`, { headers: hdr() }),
        fetch(`${API}/api/gps/journal`, { headers: hdr() }),
        fetch(`${API}/api/gps/career-explorations`, { headers: hdr() }),
        fetch(`${API}/api/gps/post-secondary`, { headers: hdr() }),
        fetch(`${API}/api/gps/courses`, { headers: hdr() }),
      ]);
      if (dR.ok) setDash(await dR.json());
      if (aR.ok) setActivities(await aR.json());
      if (jR.ok) setJournal(await jR.json());
      if (cR.ok) setCareers(await cR.json());
      if (pR.ok) setPostSec(await pR.json());
      if (wR.ok) setCourses(await wR.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGuidance = useCallback(async (section) => {
    try {
      const r = await fetch(`${API}/api/gps/ai-guidance?section=${section}`, { headers: hdr() });
      if (r.ok) {
        const data = await r.json();
        setGuidance(prev => ({ ...prev, [section]: data }));
      }
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [fetchAll]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGuidance(activeTab); }, [activeTab, fetchGuidance]);

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); };

  const handleAIAction = (action) => {
    if (action === 'discover' || action === 'interests') setActiveTab('discover');
    else if (action === 'explore' || action === 'career') setActiveTab('explore');
    else if (action === 'goals') navigate('/goals');
    else if (action === 'journal') setShowJournalModal(true);
    else if (action === 'post-secondary') setShowPostSecModal(true);
  };

  const discoverCareers = async () => {
    setDiscovering(true);
    try {
      const r = await fetch(`${API}/api/gps/discover-careers`, { method: 'POST', headers: hdr() });
      if (r.ok) {
        const d = await r.json();
        if (d.careers && d.careers.length > 0) {
          setDiscoveredCareers(d.careers);
          setDiscoverMsg(d.message || '');
        } else {
          toast.error(d.message || "Couldn't generate matches right now — try again!");
          setDiscoverMsg('');
        }
      }
    } catch (e) { toast.error('Connection issue — try again'); }
    setDiscovering(false);
  };

  const saveCareer = async (career) => {
    const r = await fetch(`${API}/api/gps/career-explore`, { method: 'POST', headers: hdr(), body: JSON.stringify({
      career_title: career.title, career_description: career.description, salary_range: career.salary_range,
      education_path: career.education_path, skills_needed: career.skills_needed, source: 'ai_recommendation'
    })});
    if (r.ok) { toast.success(`Saved ${career.title}! +5 pts`); fetchAll(); fetchGuidance('explore'); setDiscoveredCareers(prev => prev.filter(c => c.title !== career.title)); }
  };

  const skipCareer = (career) => { setDiscoveredCareers(prev => prev.filter(c => c.title !== career.title)); };

  const submitActivity = async () => {
    if (!activityForm.title) { toast.error('Enter activity name'); return; }
    const r = await fetch(`${API}/api/gps/activities`, { method: 'POST', headers: hdr(), body: JSON.stringify(activityForm) });
    if (r.ok) { toast.success('Activity added! +10 pts'); setShowActivityModal(false); setActivityForm({ title: '', category: 'club', organization: '', role: '', description: '', is_current: true }); fetchAll(); fetchGuidance(activeTab); }
  };
  const submitJournal = async () => {
    if (!journalForm.response) { toast.error('Write something first'); return; }
    const r = await fetch(`${API}/api/gps/journal`, { method: 'POST', headers: hdr(), body: JSON.stringify(journalForm) });
    if (r.ok) { toast.success('Saved! +5 pts'); setShowJournalModal(false); setJournalForm({ prompt: '', response: '', mood: '' }); fetchAll(); fetchGuidance(activeTab); }
  };
  const submitPostSec = async () => {
    if (!postSecForm.school_name) { toast.error('Enter school name'); return; }
    const r = await fetch(`${API}/api/gps/post-secondary`, { method: 'POST', headers: hdr(), body: JSON.stringify(postSecForm) });
    if (r.ok) { toast.success('Added! +10 pts'); setShowPostSecModal(false); setPostSecForm({ school_name: '', school_type: 'college', location: '', program_of_interest: '', application_status: 'researching', deadline: '' }); fetchAll(); fetchGuidance(activeTab); }
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3" style={{ backgroundColor: '#F5F3EF' }}>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
        <Compass className="w-12 h-12 text-orange-500" />
      </motion.div>
      <p className="text-sm font-medium text-gray-500">Loading your pathway...</p>
    </div>
  );

  const PROMPTS = [
    "What's something new you learned about yourself this week?",
    "Describe a challenge you faced and how you handled it.",
    "What career or job caught your attention recently?",
    "What's one skill you want to develop?",
    "Who inspires you and why?"
  ];

  const TABS = [
    { id: 'home', label: 'Home', icon: Compass },
    { id: 'discover', label: 'Discover', icon: Heart },
    { id: 'explore', label: 'Explore', icon: Briefcase },
    { id: 'plan', label: 'Plan', icon: Target },
    { id: 'portfolio', label: 'Portfolio', icon: Award },
  ];

  const g = guidance[activeTab] || {};
  const name = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Critical Modals */}
      <ProfileCompletionModal user={user} onComplete={(u) => setUser(u)} />
      <MandatoryCheckinModal user={user} onComplete={() => fetchAll()} />
      <GoalReminders user={user} />
      {/* Top Bar */}
      <div className="sticky top-0 z-40 px-4 py-3 flex items-center justify-between shadow-sm" style={{ backgroundColor: '#14151F', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base bg-orange-500/20 border border-orange-500/30">
            {user?.avatar_emoji || '🎓'}
          </div>
          <div>
            <p className="text-white font-bold text-sm">{name}</p>
            <p className="text-gray-400 text-[10px]">{user?.program_name || 'Student Pathways GPS'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <NotificationBell />
          <button onClick={() => navigate('/ai-guide')} className="p-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 transition-colors" data-testid="ai-guide-btn">
            <Brain className="w-4 h-4 text-orange-300" />
          </button>
          <button onClick={() => navigate('/helpdesk')} className="p-2 rounded-lg bg-gray-100 hover:bg-white/20 transition-colors" data-testid="counselor-chat-btn">
            <MessageCircle className="w-4 h-4 text-gray-300" />
          </button>
          <button onClick={handleLogout} className="p-2 rounded-lg bg-gray-100 hover:bg-white/20 transition-colors">
            <LogOut className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        <AnimatePresence mode="wait">
          {/* ========== HOME TAB ========== */}
          {activeTab === 'home' && (
            <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <AIBubble {...g} onAction={handleAIAction} colors={colors} />

              {/* Pathway Progress */}
              <Card className="p-5 rounded-2xl bg-white shadow-sm rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-bold text-base text-gray-900 flex items-center gap-2">
                    <Compass className="w-5 h-5 text-orange-500" /> Your Pathway
                  </h2>
                  <div className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                    <span className="text-sm font-bold text-gray-900">{dash?.total_points || 0} pts</span>
                  </div>
                </div>
                <Progress value={dash?.pathway_progress || 0} className="h-3 mb-1" />
                <p className="text-xs text-gray-500">{dash?.pathway_progress || 0}% complete — keep going!</p>

                <div className="grid grid-cols-4 gap-2 mt-4">
                  {[
                    { label: 'Interests', val: dash?.interests_count || 0, icon: Heart, color: '#EC4899', tab: 'discover' },
                    { label: 'Goals', val: dash?.active_goals || 0, icon: Target, color: '#10B981', tab: 'plan' },
                    { label: 'Careers', val: dash?.career_explorations || 0, icon: Briefcase, color: '#8B5CF6', tab: 'explore' },
                    { label: 'Activities', val: dash?.activities_count || 0, icon: Users, color: '#3B82F6', tab: 'discover' },
                  ].map(s => (
                    <button key={s.label} onClick={() => setActiveTab(s.tab)} className="text-center p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                      <s.icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                      <p className="text-lg font-bold text-gray-900">{s.val}</p>
                      <p className="text-[10px] text-gray-500">{s.label}</p>
                    </button>
                  ))}
                </div>
              </Card>

              {/* Quick Links */}
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                {[
                  { icon: Trophy, label: 'Rankings', path: '/leaderboard', bg: '#F59E0B' },
                  { icon: Award, label: 'Wins', path: '/wins', bg: '#EC4899' },
                  { icon: Star, label: 'Assess', path: '/custom-assessments', bg: '#8B5CF6' },
                  { icon: BookOpen, label: 'Courses', path: '/workshops', bg: '#3B82F6' },
                  { icon: FolderOpen, label: 'Vault', path: '/vault', bg: '#10B981' },
                ].map(item => (
                  <button key={item.label} onClick={() => navigate(item.path)} className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl bg-white shadow-sm hover:shadow-md transition-all">
                    <item.icon className="w-4 h-4" style={{ color: item.bg }} />
                    <span className="text-xs font-bold text-gray-700">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* This Week */}
              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" /> This Week's Moves
                </h3>
                {[
                  { label: 'Write a journal entry', done: dash?.weekly_routine?.has_journal_this_week, action: () => { setJournalForm({ prompt: PROMPTS[Math.floor(Math.random() * PROMPTS.length)], response: '', mood: '' }); setShowJournalModal(true); }, pts: 5 },
                  { label: 'Review or set a goal', done: dash?.weekly_routine?.goals_updated, action: () => navigate('/goals'), pts: 10 },
                  { label: 'Explore a career', done: dash?.weekly_routine?.career_explored, action: () => navigate('/ai-counselor'), pts: 5 },
                ].map((item, i) => (
                  <div key={i} onClick={!item.done ? item.action : undefined}
                    className={`flex items-center gap-3 p-3 rounded-xl mb-2 transition-all ${item.done ? 'bg-green-50' : 'bg-gray-50 hover:bg-blue-50 cursor-pointer'}`}>
                    {item.done ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> : <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                    <span className={`text-sm flex-1 ${item.done ? 'text-green-700 line-through' : 'text-gray-700 font-medium'}`}>{item.label}</span>
                    {!item.done && <Badge className="text-[9px] bg-amber-50 text-amber-600">+{item.pts}</Badge>}
                  </div>
                ))}
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Brain, label: 'AI Career Guide', desc: 'Get personalized advice', action: () => navigate('/ai-guide'), gradient: 'linear-gradient(135deg, #E86C3D, #C4542D)' },
                  { icon: Target, label: 'Set a Goal', desc: 'Plan your next move', action: () => navigate('/goals'), gradient: 'linear-gradient(135deg, #059669, #047857)' },
                  { icon: MessageCircle, label: 'Ask Counselor', desc: 'Get help anytime', action: () => navigate('/helpdesk'), gradient: 'linear-gradient(135deg, #2563EB, #1D4ED8)' },
                  { icon: Calendar, label: 'Events', desc: 'Schedules & check-ins', action: () => navigate('/events'), gradient: 'linear-gradient(135deg, #E86C3D, #C4542D)' },
                  { icon: PenTool, label: 'Journal', desc: 'Reflect on your week', action: () => { setJournalForm({ prompt: PROMPTS[0], response: '', mood: '' }); setShowJournalModal(true); }, gradient: 'linear-gradient(135deg, #D97706, #B45309)' },
                ].map(a => (
                  <Card key={a.label} onClick={a.action} className="p-4 rounded-2xl cursor-pointer hover:shadow-md transition-all border-0 text-white" style={{ background: a.gradient }} data-testid={`action-${a.label.toLowerCase().replace(/ /g, '-')}`}>
                    <a.icon className="w-7 h-7 mb-2 text-white/90" />
                    <p className="font-bold text-sm">{a.label}</p>
                    <p className="text-[11px] text-gray-700">{a.desc}</p>
                  </Card>
                ))}
              </div>

              {/* Recent Careers */}
              {dash?.recent_careers?.length > 0 && (
                <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                  <h3 className="font-bold text-sm text-gray-900 mb-3">Recently Explored</h3>
                  {dash.recent_careers.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-orange-50 mb-1.5">
                      <Briefcase className="w-4 h-4 text-orange-500" />
                      <div className="flex-1"><p className="text-sm font-semibold text-white">{c.career_title}</p><p className="text-[10px] text-gray-500">{c.salary_range}</p></div>
                    </div>
                  ))}
                </Card>
              )}
            </motion.div>
          )}

          {/* ========== DISCOVER TAB ========== */}
          {activeTab === 'discover' && (
            <motion.div key="discover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <h2 className="text-xl font-bold text-white">Discover Yourself</h2>
              <AIBubble {...(guidance.discover || {})} onAction={handleAIAction} colors={colors} />

              <div className="grid grid-cols-2 gap-3">
                <Card onClick={() => navigate('/interests')} className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all" data-testid="discover-interests">
                  <Heart className="w-7 h-7 mb-2 text-pink-500" />
                  <p className="font-bold text-sm text-white">Interests</p>
                  <p className="text-xs text-gray-500">{dash?.interests_count || 0} discovered</p>
                  {!dash?.interests_count && <p className="text-[10px] text-pink-500 font-medium mt-1">Start here!</p>}
                </Card>
                <Card onClick={() => navigate('/skills')} className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all" data-testid="discover-skills">
                  <Zap className="w-7 h-7 mb-2 text-amber-500" />
                  <p className="font-bold text-sm text-white">Skills</p>
                  <p className="text-xs text-gray-500">{dash?.skills_count || 0} tracked</p>
                </Card>
              </div>

              {/* Activities */}
              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> My Activities</h3>
                  <Button size="sm" onClick={() => setShowActivityModal(true)} className="bg-blue-600 hover:bg-blue-700 text-gray-900 text-xs h-8 rounded-lg" data-testid="add-activity-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
                {activities.length === 0 ? (
                  <div className="text-center py-5 bg-blue-50 rounded-xl">
                    <Users className="w-8 h-8 mx-auto mb-2 text-blue-300" />
                    <p className="text-sm text-gray-600">Clubs, sports, jobs, volunteering...</p>
                    <p className="text-xs text-blue-600 font-medium mt-1">Add your first activity to unlock career connections</p>
                  </div>
                ) : (
                  <div className="space-y-2">{activities.map(a => (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                      <span className="text-lg">{a.category === 'sport' ? '⚽' : a.category === 'club' ? '🎭' : a.category === 'job' ? '💼' : a.category === 'volunteer' ? '🤝' : '📌'}</span>
                      <div className="flex-1"><p className="text-sm font-semibold text-white">{a.title}</p><p className="text-[10px] text-gray-500">{a.role || a.category} {a.organization ? `at ${a.organization}` : ''}</p></div>
                      {a.is_current && <Badge className="text-[9px] bg-green-50 text-green-700 border border-green-200">Current</Badge>}
                    </div>
                  ))}</div>
                )}
              </Card>

              {/* Journal */}
              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3"><PenTool className="w-4 h-4 text-indigo-500" /> Micro-Journal</h3>
                <div className="flex gap-2 overflow-x-auto pb-2 mb-3">{PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => { setJournalForm({ prompt: p, response: '', mood: '' }); setShowJournalModal(true); }}
                    className="flex-shrink-0 p-3 rounded-xl text-left text-xs font-medium max-w-[180px] bg-indigo-50 hover:bg-indigo-100 text-indigo-800 transition-colors border border-indigo-100">{p}</button>
                ))}</div>
                {journal.length > 0 && <div className="space-y-2">{journal.slice(0, 2).map(j => (
                  <div key={j.id} className="p-3 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2 mb-1">{j.mood && <span className="text-sm">{j.mood}</span>}<span className="text-[10px] text-gray-400">{new Date(j.created_at).toLocaleDateString()}</span></div>
                    <p className="text-xs text-gray-700">{j.response?.slice(0, 120)}{j.response?.length > 120 ? '...' : ''}</p>
                  </div>
                ))}</div>}
              </Card>

              {/* Courses */}
              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-emerald-500" /> YAS Courses</h3>
                {courses.map(ws => (
                  <div key={ws.id} onClick={() => setShowWorkshop(ws)}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer ${ws.completed ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-gray-50 hover:bg-emerald-50'} transition-colors`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ws.completed ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      {ws.completed ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Play className="w-5 h-5 text-gray-500" />}
                    </div>
                    <div className="flex-1"><p className="text-sm font-semibold text-white">{ws.title}</p><p className="text-[10px] text-gray-500">{ws.duration_minutes} min | +{ws.points_earned} pts</p></div>
                    {ws.completed ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Done</Badge> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </div>
                ))}
              </Card>
            </motion.div>
          )}

          {/* ========== EXPLORE TAB — Career Discovery Engine ========== */}
          {activeTab === 'explore' && (
            <motion.div key="explore" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <h2 className="text-xl font-bold text-white">Explore Careers</h2>
              <AIBubble {...(guidance.explore || {})} onAction={handleAIAction} colors={colors} />

              {/* AI Career Discovery — The Core Feature */}
              {discoveredCareers.length === 0 ? (
                <Card className="p-6 rounded-2xl border-0 overflow-hidden relative text-center"
                  style={{ background: 'linear-gradient(135deg, #E86C3D 0%, #E86C3D 50%, #A855F7 100%)' }}>
                  <div className="relative z-10">
                    <Brain className="w-12 h-12 mx-auto mb-3 text-white/90" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {!dash?.interests_count ? "First, tell me about yourself" : "Ready to discover your career matches?"}
                    </h3>
                    <p className="text-white/75 text-sm mb-4 max-w-sm mx-auto">
                      {!dash?.interests_count 
                        ? "Head to the Discover tab and add your interests — then I can show you careers that actually fit who you are."
                        : "I'll look at your interests, skills, and activities and find careers you might never have thought of."}
                    </p>
                    <Button onClick={!dash?.interests_count ? () => setActiveTab('discover') : discoverCareers}
                      disabled={discovering}
                      className="bg-white text-orange-700 hover:bg-gray-100 font-bold px-6 py-3 rounded-xl shadow-lg"
                      data-testid="discover-careers-btn">
                      {discovering ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding your matches...</>
                        : !dash?.interests_count ? <>Go to Discover <ArrowRight className="w-4 h-4 ml-2" /></>
                        : <>Find My Career Matches <Sparkles className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </div>
                  <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gray-100" />
                  <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-gray-100" />
                </Card>
              ) : (
                <>
                  {discoverMsg && <p className="text-sm text-gray-600 italic">{discoverMsg}</p>}
                  <div className="space-y-3">
                    {discoveredCareers.map((career, idx) => (
                      <motion.div key={career.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
                        <Card className="p-5 rounded-2xl bg-white shadow-sm rounded-2xl">
                          <h4 className="font-bold text-base text-gray-900 mb-1">{career.title}</h4>
                          <p className="text-sm text-gray-600 mb-3 leading-relaxed">{career.description}</p>
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {(career.skills_needed || []).map((sk, i) => (
                              <Badge key={i} className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200">{sk}</Badge>
                            ))}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                            <span className="flex items-center gap-1">💰 {career.salary_range}</span>
                            <span className="flex items-center gap-1">🎓 {career.education_path}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => saveCareer(career)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-gray-900 rounded-xl font-bold" data-testid={`save-career-${idx}`}>
                              <Heart className="w-4 h-4 mr-1" /> Save This
                            </Button>
                            <Button onClick={() => skipCareer(career)} variant="outline" className="rounded-xl text-gray-500">
                              Not for me
                            </Button>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                  <Button onClick={discoverCareers} disabled={discovering} variant="outline" className="w-full rounded-xl" data-testid="more-careers-btn">
                    {discovering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Show me more options
                  </Button>
                </>
              )}

              {/* Career Clusters */}
              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <h3 className="font-bold text-sm text-gray-900 mb-3">Browse by Category</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { name: 'Technology', icon: '💻' }, { name: 'Healthcare', icon: '🏥' },
                    { name: 'Business', icon: '📊' }, { name: 'Creative Arts', icon: '🎨' },
                    { name: 'Education', icon: '📚' }, { name: 'Trades & Skilled', icon: '🔧' },
                    { name: 'Public Service', icon: '🏛️' }, { name: 'Science', icon: '🔬' },
                  ].map(c => (
                    <button key={c.name} onClick={() => navigate('/ai-counselor')} className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left">
                      <span className="text-lg">{c.icon}</span><span className="text-xs font-semibold text-white">{c.name}</span>
                    </button>
                  ))}
                </div>
              </Card>

              {/* My Career Board (Saved) */}
              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-pink-500" fill="currentColor" /> My Career Board ({careers.length})
                </h3>
                {careers.length === 0 ? (
                  <div className="text-center py-5 bg-pink-50 rounded-xl">
                    <Heart className="w-8 h-8 mx-auto mb-2 text-pink-300" />
                    <p className="text-sm text-gray-600">Careers you save will appear here</p>
                    <p className="text-xs text-pink-600 font-medium mt-1">Your counselor and parents can see your saved careers too</p>
                  </div>
                ) : (
                  <div className="space-y-2">{careers.map(c => (
                    <div key={c.id} className="p-3 rounded-xl bg-pink-50 border border-pink-100">
                      <p className="font-semibold text-sm text-white">{c.career_title}</p>
                      <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-1"><span>💰 {c.salary_range}</span><span>🎓 {c.education_path}</span></div>
                    </div>
                  ))}</div>
                )}
              </Card>

              {/* AI Counselor deep dive */}
              <Card onClick={() => navigate('/ai-counselor')} className="p-4 rounded-2xl bg-gray-50 border-0 cursor-pointer hover:bg-gray-100 transition-all">
                <div className="flex items-center gap-3">
                  <Brain className="w-8 h-8 text-orange-500" />
                  <div className="flex-1">
                    <p className="font-bold text-sm text-white">Talk to AI Career Counselor</p>
                    <p className="text-xs text-gray-500">Ask anything about careers, education, or your future</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </Card>
            </motion.div>
          )}

          {/* ========== PLAN TAB ========== */}
          {activeTab === 'plan' && (
            <motion.div key="plan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <h2 className="text-xl font-bold text-white">Plan Your Future</h2>
              <AIBubble {...(guidance.plan || {})} onAction={handleAIAction} colors={colors} />

              <Card onClick={() => navigate('/goals')} className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all" data-testid="plan-goals">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center"><Target className="w-6 h-6 text-emerald-500" /></div>
                  <div className="flex-1">
                    <p className="font-bold text-white">My Goals</p>
                    <p className="text-xs text-gray-500">{dash?.active_goals || 0} active, {dash?.completed_goals || 0} completed</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </Card>

              <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-indigo-500" /> After High School</h3>
                  <Button size="sm" onClick={() => setShowPostSecModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-gray-900 text-xs h-8 rounded-lg" data-testid="add-school-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
                {postSec.length === 0 ? (
                  <div className="text-center py-6 bg-indigo-50 rounded-xl">
                    <GraduationCap className="w-10 h-10 mx-auto mb-2 text-indigo-300" />
                    <p className="text-sm text-gray-600">College, trade school, apprenticeship, military?</p>
                    <p className="text-xs text-indigo-600 font-medium mt-1">Start researching — add your first option</p>
                  </div>
                ) : (
                  <div className="space-y-2">{postSec.map(ps => (
                    <div key={ps.id} className="p-3 rounded-xl bg-gray-50">
                      <div className="flex items-center justify-between mb-1"><p className="font-semibold text-sm text-white">{ps.school_name}</p>
                        <Badge className="text-[9px]" style={{ backgroundColor: ps.application_status === 'accepted' ? '#D1FAE5' : ps.application_status === 'applied' ? '#DBEAFE' : '#F3F4F6', color: ps.application_status === 'accepted' ? '#065F46' : ps.application_status === 'applied' ? '#1E40AF' : '#6B7280' }}>{ps.application_status?.replace('_', ' ')}</Badge>
                      </div>
                      <p className="text-[10px] text-gray-500">{ps.school_type?.replace('_', ' ')} {ps.location ? `— ${ps.location}` : ''}</p>
                      {ps.deadline && <p className="text-[10px] text-red-500 mt-1">Deadline: {new Date(ps.deadline).toLocaleDateString()}</p>}
                    </div>
                  ))}</div>
                )}
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <Card onClick={() => navigate('/helpdesk')} className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all">
                  <MessageCircle className="w-6 h-6 mb-2 text-blue-500" />
                  <p className="font-bold text-sm text-white">Ask Counselor</p>
                  <p className="text-[10px] text-gray-500">Get guidance anytime</p>
                </Card>
                <Card onClick={() => navigate('/resources')} className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all">
                  <FolderOpen className="w-6 h-6 mb-2 text-amber-500" />
                  <p className="font-bold text-sm text-white">Resources</p>
                  <p className="text-[10px] text-gray-500">Guides & helpful links</p>
                </Card>
              </div>
            </motion.div>
          )}

          {/* ========== PORTFOLIO TAB ========== */}
          {activeTab === 'portfolio' && (
            <motion.div key="portfolio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <h2 className="text-xl font-bold text-white">My Portfolio</h2>
              <AIBubble {...(guidance.portfolio || {})} onAction={handleAIAction} colors={colors} />

              {/* Story Header */}
              <Card className="p-5 rounded-2xl border-0 text-white overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #1E293B, #334155)' }}>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center text-2xl border border-white/20">{user?.avatar_emoji || '🎓'}</div>
                    <div>
                      <h3 className="text-lg font-bold">{user?.name}</h3>
                      <p className="text-gray-400 text-sm">{user?.program_name || 'Student Pathways GPS'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { label: 'Points', val: dash?.total_points || 0, icon: Star },
                      { label: 'Interests', val: dash?.interests_count || 0, icon: Heart },
                      { label: 'Goals Hit', val: dash?.completed_goals || 0, icon: Trophy },
                      { label: 'Careers', val: dash?.career_explorations || 0, icon: Briefcase },
                    ].map(s => (
                      <div key={s.label} className="text-center p-2 rounded-lg bg-white/10">
                        <s.icon className="w-3.5 h-3.5 mx-auto mb-1 text-white/70" />
                        <p className="text-base font-bold">{s.val}</p>
                        <p className="text-[9px] text-white/60">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-orange-500/20" />
              </Card>

              {/* Activities in Portfolio */}
              {activities.length > 0 && (
                <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                  <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-blue-500" /> Activities & Experience</h3>
                  {activities.map(a => (
                    <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 mb-1.5">
                      <span className="text-base">{a.category === 'sport' ? '⚽' : a.category === 'club' ? '🎭' : a.category === 'job' ? '💼' : '📌'}</span>
                      <div className="flex-1"><p className="text-sm font-semibold text-white">{a.title}</p><p className="text-[10px] text-gray-500">{a.role} {a.organization ? `at ${a.organization}` : ''}</p></div>
                    </div>
                  ))}
                </Card>
              )}

              {/* Career Interests */}
              {careers.length > 0 && (
                <Card className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl">
                  <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3"><Briefcase className="w-4 h-4 text-orange-500" /> Career Interests</h3>
                  {careers.map(c => (
                    <div key={c.id} className="p-2.5 rounded-lg bg-orange-50 mb-1.5">
                      <p className="text-sm font-semibold text-white">{c.career_title}</p>
                      <p className="text-[10px] text-gray-500">{c.salary_range} | {c.education_path}</p>
                    </div>
                  ))}
                </Card>
              )}

              {/* Links to deeper pages */}
              <div className="space-y-2">
                {[
                  { icon: FolderOpen, label: 'Digital Vault', desc: 'Certificates, work samples, projects', route: '/vault', color: '#3B82F6' },
                  { icon: Award, label: 'Accomplishments', desc: 'Milestones and achievements', route: '/accomplishments', color: '#F59E0B' },
                  { icon: Star, label: 'My Badges', desc: 'Earned through your journey', route: '/badges', color: '#10B981' },
                ].map(item => (
                  <Card key={item.label} onClick={() => navigate(item.route)} className="p-4 rounded-2xl bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}10` }}>
                        <item.icon className="w-5 h-5" style={{ color: item.color }} />
                      </div>
                      <div className="flex-1"><p className="font-bold text-sm text-white">{item.label}</p><p className="text-[10px] text-gray-500">{item.desc}</p></div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom Nav */}
      <div className="fixed bottom-0 left-0 right-0 border-t px-2 py-1.5 z-50 backdrop-blur-lg" style={{ backgroundColor: 'rgba(27,45,79,0.97)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-2xl mx-auto flex justify-around">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all"
              style={{ backgroundColor: activeTab === tab.id ? '#FFF0EA' : 'transparent', color: activeTab === tab.id ? '#E86C3D' : '#8A96A3' }}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-5 h-5" />
              <span className="text-[10px] font-bold">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Modals */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent className="rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-500" /> Add Activity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Activity Name *</Label><Input value={activityForm.title} onChange={e => setActivityForm({...activityForm, title: e.target.value})} placeholder="Basketball Team, Coding Club..." className="mt-1" /></div>
            <div><Label>Category</Label><Select value={activityForm.category} onValueChange={v => setActivityForm({...activityForm, category: v})}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{['club', 'sport', 'volunteer', 'job', 'internship', 'arts', 'academic', 'leadership', 'other'].map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Organization</Label><Input value={activityForm.organization} onChange={e => setActivityForm({...activityForm, organization: e.target.value})} placeholder="School name, company..." className="mt-1" /></div>
            <div><Label>Your Role</Label><Input value={activityForm.role} onChange={e => setActivityForm({...activityForm, role: e.target.value})} placeholder="Team Captain, Volunteer..." className="mt-1" /></div>
            <Button onClick={submitActivity} className="w-full bg-blue-600 hover:bg-blue-700">Add Activity (+10 pts)</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showJournalModal} onOpenChange={setShowJournalModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PenTool className="w-5 h-5 text-indigo-500" /> Quick Journal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {journalForm.prompt && <div className="p-3 rounded-xl bg-indigo-50 text-sm text-indigo-800 font-medium">{journalForm.prompt}</div>}
            <div className="flex gap-2">{['😊', '😐', '😤', '🤔', '💪'].map(m => (
              <button key={m} onClick={() => setJournalForm({...journalForm, mood: m})} className={`text-2xl p-2 rounded-lg transition-all ${journalForm.mood === m ? 'bg-indigo-100 scale-110 ring-2 ring-indigo-300' : 'hover:bg-gray-100'}`}>{m}</button>
            ))}</div>
            <Textarea value={journalForm.response} onChange={e => setJournalForm({...journalForm, response: e.target.value})} placeholder="What's on your mind?" rows={4} />
            <Button onClick={submitJournal} className="w-full bg-indigo-600 hover:bg-indigo-700">Save Entry (+5 pts)</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPostSecModal} onOpenChange={setShowPostSecModal}>
        <DialogContent className="rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-indigo-500" /> Add School / Program</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>School / Program Name *</Label><Input value={postSecForm.school_name} onChange={e => setPostSecForm({...postSecForm, school_name: e.target.value})} placeholder="Rutgers, NJIT, trade school..." className="mt-1" /></div>
            <div><Label>Type</Label><Select value={postSecForm.school_type} onValueChange={v => setPostSecForm({...postSecForm, school_type: v})}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="college">4-Year College</SelectItem><SelectItem value="community_college">Community College</SelectItem><SelectItem value="trade_school">Trade School</SelectItem><SelectItem value="apprenticeship">Apprenticeship</SelectItem><SelectItem value="military">Military</SelectItem><SelectItem value="gap_year">Gap Year</SelectItem></SelectContent></Select></div>
            <div><Label>Location</Label><Input value={postSecForm.location} onChange={e => setPostSecForm({...postSecForm, location: e.target.value})} placeholder="New Brunswick, NJ" className="mt-1" /></div>
            <div><Label>Program of Interest</Label><Input value={postSecForm.program_of_interest} onChange={e => setPostSecForm({...postSecForm, program_of_interest: e.target.value})} placeholder="Computer Science, Nursing..." className="mt-1" /></div>
            <div><Label>Status</Label><Select value={postSecForm.application_status} onValueChange={v => setPostSecForm({...postSecForm, application_status: v})}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="researching">Researching</SelectItem><SelectItem value="planning_to_apply">Planning to Apply</SelectItem><SelectItem value="applied">Applied</SelectItem><SelectItem value="accepted">Accepted</SelectItem><SelectItem value="enrolled">Enrolled</SelectItem></SelectContent></Select></div>
            <div><Label>Deadline</Label><Input type="date" value={postSecForm.deadline} onChange={e => setPostSecForm({...postSecForm, deadline: e.target.value})} className="mt-1" /></div>
            <Button onClick={submitPostSec} className="w-full bg-indigo-600 hover:bg-indigo-700">Add (+10 pts)</Button>
          </div>
        </DialogContent>
      </Dialog>

      {showWorkshop && (
        <Dialog open={!!showWorkshop} onOpenChange={() => setShowWorkshop(null)}>
          <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>{showWorkshop.title}</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-500 mb-4">{showWorkshop.description}</p>
            <WorkshopPlayer course={showWorkshop} onComplete={() => { setShowWorkshop(null); fetchAll(); fetchGuidance(activeTab); }} />
          </DialogContent>
        </Dialog>
      )}

      <Navigation role="youth" />
    </div>
  );
};

const WorkshopPlayer = ({ course, onComplete }) => {
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState(course.saved_responses || {});
  const [submitting, setSubmitting] = useState(false);
  const isReview = course.completed;
  const section = course.sections[step];
  const isLast = step === course.sections.length - 1;
  const totalSteps = course.sections.length;

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/gps/courses/${course.id}/complete`, { method: 'POST', headers: hdr(), body: JSON.stringify({ responses }) });
      if (r.ok) { toast.success(`Workshop complete! +${course.points_earned} pts`); onComplete(); } else { const d = await r.json(); toast.error(d.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      {/* Progress bar with step counter */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex gap-1 flex-1">{course.sections.map((_, i) => (
          <button key={i} onClick={() => setStep(i)} className={`h-2 flex-1 rounded-full transition-all cursor-pointer hover:opacity-80 ${i <= step ? 'bg-orange-500' : i < step ? 'bg-orange-300' : 'bg-gray-200'}`} />
        ))}</div>
        <span className="text-xs text-gray-400 flex-shrink-0">{step + 1}/{totalSteps}</span>
      </div>

      {/* Review mode banner */}
      {isReview && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <p className="text-xs text-emerald-700 font-medium">Completed — reviewing your responses</p>
        </div>
      )}

      {/* Section header with animation */}
      <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
        <h3 className="font-bold text-lg text-white">{section.title}</h3>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">{section.prompt}</p>

        {/* Interactive elements based on type */}
        <div className="mt-4">
          {section.type === 'reflection' && (
            <Textarea
              value={responses[section.title] || ''}
              onChange={e => !isReview && setResponses({...responses, [section.title]: e.target.value})}
              placeholder={isReview ? '' : "Share your thoughts..."}
              rows={4}
              readOnly={isReview}
              className={`rounded-xl ${isReview ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100'}`}
            />
          )}

          {section.type === 'checklist' && (
            <div className="space-y-2">{section.items.map((item, i) => {
              const checked = (responses[section.title] || []).includes(item);
              return (
                <motion.label key={i} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className={`flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition-all ${checked ? 'bg-orange-50 border-2 border-orange-300 shadow-sm' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'}`}>
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${checked ? 'bg-orange-500' : 'border-2 border-gray-300'}`}>
                    {checked && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className={`text-sm ${checked ? 'font-semibold text-orange-900' : 'text-gray-700'}`}>{item}</span>
                  {!isReview && (
                    <input type="checkbox" className="sr-only" checked={checked}
                      onChange={e => { const c = responses[section.title] || []; setResponses({...responses, [section.title]: e.target.checked ? [...c, item] : c.filter(x => x !== item)}); }} />
                  )}
                </motion.label>
              );
            })}</div>
          )}

          {section.type === 'ranking' && (
            <div className="space-y-2">{section.items.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-3.5 rounded-xl bg-gradient-to-r from-violet-50 to-white border border-orange-100">
                <span className="w-7 h-7 rounded-lg bg-orange-500 text-white flex items-center justify-center text-xs font-bold shadow-sm">{i + 1}</span>
                <span className="text-sm font-medium text-gray-800">{item}</span>
              </motion.div>
            ))}</div>
          )}
        </div>
      </motion.div>

      {/* Navigation buttons */}
      <div className="flex gap-2 pt-3 border-t border-gray-200">
        <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          className="rounded-xl disabled:opacity-30">
          Back
        </Button>
        <div className="flex-1" />
        {isReview ? (
          <Button onClick={() => step < totalSteps - 1 ? setStep(step + 1) : onComplete()} className="bg-orange-600 hover:bg-orange-700 rounded-xl">
            {isLast ? 'Close' : 'Next'}
          </Button>
        ) : isLast ? (
          <Button onClick={handleComplete} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold px-6">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete Workshop'}
          </Button>
        ) : (
          <Button onClick={() => setStep(step + 1)} className="bg-orange-600 hover:bg-orange-700 rounded-xl px-6">
            Next
          </Button>
        )}
      </div>
    </div>
  );
};

export default GPSStudentDashboard;
